/**FOR OTHER ALPHABETS ADD A MAP TABLE */

import { inject } from 'dependable-react';
import { APP_DATA_TOKEN } from '../services/InjectTokens';

/** KEY={CYRL} VALUE={LAT} */
const srCyrlToLatUnicodeTable = {
  //Small letters
  1072: '97',
  1073: '98',
  1094: '99',
  1095: '269',
  1115: '263',
  1076: '100',
  1119: '100 382', //dž
  1106: '273',
  1077: '101',
  1092: '102',
  1075: '103',
  1093: '104',
  1080: '105',
  1112: '106',
  1082: '107',
  1083: '108',
  1113: '108 106', //lj
  1084: '109',
  1085: '110',
  1114: '110 106', //nj
  1086: '111',
  1087: '112',
  1088: '114',
  1089: '115',
  1096: '353',
  1090: '116',
  1091: '117',
  1074: '118',
  1079: '122',
  1078: '382',
  // Big letters
  1040: '65',
  1041: '66',
  1062: '67',
  1063: '268',
  1035: '262',
  1044: '68',
  1039: '68 382', //Dž
  1026: '272',
  1045: '69',
  1060: '70',
  1043: '71',
  1061: '72',
  1048: '73',
  1032: '74',
  1050: '75',
  1051: '76',
  1033: '76 106', //Lj
  1052: '77',
  1053: '78',
  1034: '78 106', //Nj
  1054: '79',
  1055: '80',
  1056: '82',
  1057: '83',
  1064: '352',
  1058: '84',
  1059: '85',
  1042: '86',
  1047: '90',
  1046: '381',
};

const firstLetterCodes = ['100', '68', '110', '78', '108', '76']; // d, D, n, N, l, L
const secondLetterCodes = ['106', '74', '382', '381']; // j, J, ž, Ž

const getSpecialCharacters = () => {
  /**
   * Includes unicode from 32 to 64
   * Check values on https://www.rapidtables.com/code/text/unicode-characters.html
   */
  const specialCharactersArray = [];
  for (let i = 32; i <= 64; i++) {
    specialCharactersArray.push(i.toString());
  }
  /**
   * Add exceptions into the exceptionCharacters array as strings
   */
  const exceptionCharacters: Array<string> = [
    '10', // backspace
    '81', // Q
    '87', // W
    '88', // X
    '89', // Y
    '113', // q
    '119', // w
    '120', // x
    '121', // y
    '229',
    '228',
    '246',
    '197',
    '196',
    '214',
    '233',
    '201',
  ];

  const germanLetters: Array<string> = ['228', '196', '246', '214', '252', '220', '223'];
  const polishLetters: Array<string> = [
    '261',
    '260',
    '281',
    '280',
    '322',
    '321',
    '324',
    '323',
    '243',
    '211',
    '347',
    '346',
    '378',
    '377',
    '380',
    '379',
  ];
  return [...specialCharactersArray, ...exceptionCharacters, ...germanLetters, ...polishLetters];
};

const toUnicode = (word: string): Array<number> => {
  const chars = Array.from(word);
  const result = [];

  for (let i = 0; i < chars.length; i++) {
    result.push(word.charCodeAt(i));
  }

  return result;
};

const toAlphabet = (unicode: Array<number>) => {
  return String.fromCharCode(...unicode);
};

const convert = (values: Array<string>) => {
  const checkedValues: Array<string> = [];
  values.map((value) => {
    if (value.match(/ /g)) {
      const separatedValues = value.trim().split(' ');
      separatedValues.map((separatedValue) => checkedValues.push(separatedValue));
    } else {
      checkedValues.push(value);
    }
  });

  return checkedValues.map((value) => {
    return parseInt(value, 10);
  });
};

const selectUnicodeTable = (target: string) => {
  const table = {};
  switch (target) {
    case 'srCyrl':
      Object.assign(table, srCyrlToLatUnicodeTable);
      break;
    /**ADD HERE NEW LETTER TABLE CODES e.g. ru, bul */
    default:
      Object.assign(table, srCyrlToLatUnicodeTable);
      break;
  }
  return table;
};

export function translateAlphabet(word: string, targetLanguage = 'srCyrl'): string {
  const { currentLanguage } = inject(APP_DATA_TOKEN);

  const specialCharacters = getSpecialCharacters();
  const codeStrings: Array<string> = [];
  const unicodeTable = selectUnicodeTable(targetLanguage);
  const unicode = toUnicode(word);

  for (let index = 0; index <= unicode.length; index++) {
    if (unicode[index]) {
      const codeValue = unicode[index].toString();
      let nextCodeValue = unicode[index + 1] && unicode[index + 1].toString();

      if (specialCharacters.includes(codeValue)) {
        codeStrings.push(codeValue);
      }

      if (
        targetLanguage === currentLanguage &&
        nextCodeValue &&
        firstLetterCodes.includes(codeValue) &&
        secondLetterCodes.includes(nextCodeValue)
      ) {
        /**
         * Handles unreal situations
         * if the users input is LJ NJ or DŽ <== grammaticly incorrect
         */
        if (nextCodeValue === '381') {
          nextCodeValue = '382';
        }
        if (nextCodeValue === '74') {
          nextCodeValue = '106';
        }
        const multiLetterCode = codeValue.concat(' ', nextCodeValue);

        if (multiLetterCode) {
          index++;
          Object.entries(unicodeTable).map(([key, value]) => {
            if (value === multiLetterCode) {
              return codeStrings.push(key);
            }
          });
        }
      } else {
        Object.entries(unicodeTable).map(([key, value]) => {
          if (codeValue === key && currentLanguage !== targetLanguage) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            return codeStrings.push(value);
          } else if (codeValue === value && currentLanguage === targetLanguage) {
            return codeStrings.push(key);
          } else if (codeValue === value && currentLanguage !== targetLanguage) {
            return codeStrings.push(value);
          } else if (codeValue === key && currentLanguage === targetLanguage) {
            return codeStrings.push(key);
          }
        });
      }
    }
  }

  const codes = convert(codeStrings);

  return toAlphabet(codes);
}
